import { DataGridPremium, GridActionsCellItem, type GridColDef, useGridApiRef } from '@mui/x-data-grid-premium'
import type React from 'react'
import { useRef, useState } from 'react'
import { useAccountingAccounts, useAccountingYears, useDeleteAccountingAccount } from '../api/react-query'
import type { AccountDto } from '../api/swagger/definitions/backoffice'
import ColorsDeprecated from '../figma/tokens/ColorsDeprecated'
import Spacings from '../figma/tokens/Spacings'
import useLanguage from '../flamingo/hooks/useLanguage'
import { getAccountsTableActions } from '../helpers/AccountingTablesHelper'
import { TableHeader, useTableGridProps } from '../helpers/getTableOptions'
import { captureException } from '../helpers/sentryHelpers'
import ImageContainer from '../mynt-components/components/ImageContainer'
import MyntPopper from '../mynt-components/components/MyntPopper'
import { getText, TextKeys } from '../tiger/libs/TextRepository'
import { AccountFormTypes } from './ModalAccountingAccount'
import { ModalAccountingAccount, ModalConfirmDialog } from './Modals'
import TableWrapper from './TableWrapper'

const AccountsTable: React.FC = () => {
  const language = useLanguage()
  const [accountValues, setAccountValues] = useState<AccountDto>()
  const [isAccountModalOpen, setIsAccountModalOpen] = useState<boolean>(false)
  const [isAccountDeleteModalOpen, setIsAccountDeleteModalOpen] = useState<boolean>(false)
  const divRef = useRef(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(divRef.current)

  const { data: years = [] } = useAccountingYears({
    refetchOnWindowFocus: false,
  })
  const { data: accounts = [], isLoading: accountsLoading } = useAccountingAccounts(undefined, {
    refetchOnWindowFocus: false,
  })

  const deleteAccountingAccountMutation = useDeleteAccountingAccount()

  const gridProps = useTableGridProps({ headerTextKey: TextKeys.accountingPaymentsOCR })

  const apiRef = useGridApiRef()

  const handleDeleteClick = (account: AccountDto) => {
    setIsAccountDeleteModalOpen(true)
    setAccountValues(account)
  }

  const handleEditClick = (account: AccountDto) => {
    setIsAccountModalOpen(true)
    setAccountValues(account)
  }

  const rowActions = getAccountsTableActions(handleEditClick, handleDeleteClick, setAnchorEl)

  const columns: GridColDef<AccountDto>[] = [
    { headerName: getText(TextKeys.editAccountNameLabel, language), field: 'name', flex: 1 },
    { headerName: getText(TextKeys.editAccountNumberLabel, language), field: 'no', flex: 1 },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({ row }) =>
        rowActions.map((action, index) => (
          <GridActionsCellItem
            key={index === 0 ? 'edit' : 'delete'}
            icon={<ImageContainer imageKey={action.iconProps.imageKey} />}
            label={index === 0 ? 'Edit' : 'Delete'}
            onClick={(event) => action.onClick(event, row)}
          />
        )),
    },
  ]

  const handleDeleteAccept = async () => {
    handleClearModal()

    deleteAccountingAccountMutation.mutateAsync({ id: accountValues?.id as string }).catch((err) => captureException(err))
  }

  const handleClearModal = () => {
    setIsAccountDeleteModalOpen(false)
    setAccountValues(undefined)
  }

  return (
    <>
      {isAccountModalOpen && (
        <ModalAccountingAccount
          isOpen={isAccountModalOpen}
          type={accountValues ? AccountFormTypes.EDIT : AccountFormTypes.CREATE}
          initialValues={accountValues}
          years={years}
          onModalCancel={() => {
            setIsAccountModalOpen(false)
            setAccountValues(undefined)
          }}
        />
      )}
      <MyntPopper
        visible={isAccountDeleteModalOpen}
        noPadding
        marginBottom={Spacings.large}
        position="top"
        anchorRef={anchorEl}
        content={<ModalConfirmDialog onCancel={handleClearModal} onConfirm={handleDeleteAccept} />}
      />
      <TableWrapper>
        <TableHeader title="Accounts" apiRef={apiRef} />
        <DataGridPremium
          {...gridProps}
          apiRef={apiRef}
          rows={accounts}
          columns={columns}
          loading={accountsLoading}
          initialState={{
            ...gridProps.initialState,
            sorting: { sortModel: [{ field: 'no', sort: 'asc' }] },
          }}
          sx={{
            ...gridProps.sx,
            '& .MuiDataGrid-columnHeader': { backgroundColor: ColorsDeprecated.surfaceOnWhite },
          }}
        />
      </TableWrapper>
    </>
  )
}

export default AccountsTable
